/**
 * Il Quaderno: un foglio bianco su cui il calcolo si scrive nell'ordine in cui
 * lo si pensa, e che è già il documento da stampare.
 *
 * Un quaderno è una **sequenza di blocchi**. Ogni blocco è una riga di calcolo,
 * una nota, uno schema incollato o un capitolo ripreso da un'altra scheda. Si
 * aggiungono dove servono — in coda o fra due blocchi già scritti — e si
 * riordinano: l'ordine racconta la sequenza del calcolo, e ripensarci a metà
 * foglio è la regola, non l'eccezione. I nomi si rileggono nell'ordine nuovo,
 * quindi spostare un blocco sopra a chi lo usa fa tornare i conti.
 *
 * I blocchi di calcolo **non salvano il proprio valore**: salvano da dove viene
 * (la grandezza, la formula preimpostata, la scheda) e lo ricalcolano ogni
 * volta. Correggere la base della trave nel pannello a destra aggiorna da solo
 * tutto quello che ne discende, senza toccare il quaderno.
 *
 * Un blocco di calcolo con un nome valido diventa una variabile per i blocchi
 * che vengono dopo: `A = b·h` e poi `σ = N/A`, come si scriverebbe a mano.
 *
 * L'unità con cui leggere il risultato è del blocco, non della formula: la si
 * cambia e il **numero si converte** (0,8 MPa → 8,16 kg/cmq). Le uniche cose
 * che un blocco salva per sé sono il testo di una nota e l'immagine di uno
 * schema.
 */

import {
  formattaIn,
  haOperazioni,
  leggiRisultato,
  nomeAmmesso,
  nomiMancanti,
  nomiRichiesti,
  risolviNome,
  valutaConUnita,
  variabili,
  unitaVariabili,
  umVariabili,
  type Preimpostata,
  type VoceCalcolata,
  type VoceCalcolo,
} from './calcolatrice';
import { UNITA_DEFAULT, dimUnita, inBase, unitaCompatibili, type Dim } from './unita';

/**
 * Che cosa può stare su una pagina:
 *  - `valore`, `operazione`, `import`: una riga di calcolo collegata alla sua
 *    fonte (una variabile del pannello, una formula preimpostata, un risultato
 *    di un'altra scheda);
 *  - `formula`: una riga scritta qui, con il suo nome e la sua unità;
 *  - `nota` e `immagine`: quello che si aggiunge a mano;
 *  - `linea`: la riga orizzontale che separa un capitolo dal successivo, con
 *    il suo titolo scritto lì sopra;
 *  - `capitolo`: un capitolo intero ripreso da un'altra scheda, come faceva la
 *    spunta della vecchia scheda Esporta.
 */
export type TipoBlocco =
  | 'valore'
  | 'operazione'
  | 'formula'
  | 'import'
  | 'nota'
  | 'immagine'
  | 'linea'
  | 'capitolo';

export interface BloccoQuaderno {
  id: string;
  tipo: TipoBlocco;
  /** Fonte a cui il blocco resta collegato: id della grandezza, della formula, dell'import o del capitolo. */
  fonte: string;
  /** Nome del risultato: si può correggere solo sui blocchi `formula`. */
  nome: string;
  /** Espressione dei blocchi `formula`. */
  espressione: string;
  /** Unità con cui si vuole leggere il risultato; vuota = quella della fonte. */
  um: string;
  /** Testo della nota, o didascalia dell'immagine. */
  testo: string;
  /**
   * Nota scritta a mano su questo passaggio: il «perché» che a rileggere il
   * foglio fra sei mesi è l'unica cosa che non si ricostruisce. Si apre con
   * la (i) del blocco e finisce anche nella stampa, sotto la riga.
   */
  appunto: string;
  /**
   * Reliquia della vecchia griglia a tre colonne: la larghezza non si sceglie
   * più — la decide il contenuto. Il campo resta perché i fogli già salvati lo
   * portano, e riaprirli non deve perdere niente; sul foglio non si legge.
   */
  colonne: number;
  /**
   * Reliquia della vecchia griglia: i posti lasciati liberi prima del blocco.
   * Con le righe che vanno a capo da sé non ci sono più caselle da saltare —
   * il campo resta solo per non rompere i salvataggi di prima.
   */
  salto: number;
  /**
   * **Qui la linea finisce**: quello che viene dopo comincia sulla linea sotto,
   * anche se a destra ci sarebbe ancora posto. Il foglio manda a capo da sé
   * quando lo spazio finisce, ma un calcolo ha dei gruppi che si leggono
   * insieme — i dati di partenza, poi le azioni, poi la verifica — e dove
   * finisce un gruppo lo sa chi scrive, non la larghezza della carta.
   * Si mette e si toglie con **shift+invio** dalla riga.
   */
  acapo: boolean;
  /** Immagine incollata o trascinata, come data URL. */
  img: string;
  /**
   * Larghezza dell'immagine in percentuale del foglio (20…100). 0 = intera:
   * uno schema piccolo non deve occupare mezza pagina solo perché è arrivato
   * così, e la misura scelta vale anche nel file esportato.
   */
  larghezza: number;
}

/** Un blocco nuovo, con tutti i campi al loro posto. */
export function nuovoBlocco(tipo: TipoBlocco, patch: Partial<BloccoQuaderno> = {}): BloccoQuaderno {
  return {
    id: `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    tipo,
    fonte: '',
    nome: '',
    espressione: '',
    um: '',
    testo: '',
    appunto: '',
    colonne: 0,
    salto: 0,
    acapo: false,
    img: '',
    larghezza: 0,
    ...patch,
  };
}

/**
 * Quante colonne aveva la vecchia griglia del foglio. Non impagina più niente
 * — le righe vanno a capo da sé, larghe quanto il loro contenuto — ma resta
 * il limite con cui si rileggono i `colonne` e i `salto` dei fogli salvati.
 */
export const COLONNE_FOGLIO = 3;

/**
 * Un risultato pronto da tirare dentro dalle altre schede: il taglio delle
 * Sollecitazioni, l'esito di una verifica. Il valore arriva nell'unità con cui
 * la scheda di provenienza lo mostra; a convertirlo pensa il quaderno.
 */
export interface ImportoScheda {
  id: string;
  /** Nome con cui il valore diventa richiamabile nel quaderno. */
  nome: string;
  /** Come si chiama nell'elenco del pannello: «M max», «Esito taglio». */
  etichetta: string;
  /** Scheda da cui arriva, per il segno di provenienza. */
  scheda: string;
  valore: number;
  um: string;
  /** Testo al posto del numero, per quello che non è un numero (un esito). */
  testo?: string;
}

/** Tutto quello che serve a dare un valore ai blocchi del quaderno. */
export interface Sorgenti {
  /** Le grandezze del pannello, già ricalcolate (libreria compresa). */
  voci: VoceCalcolata[];
  preimpostate: Preimpostata[];
  importi: ImportoScheda[];
  elenco: string[];
}

/** Un blocco con dentro il suo valore di adesso. */
export interface BloccoCalcolato {
  blocco: BloccoQuaderno;
  /** Numero di passo mostrato sul foglio: 01, 02, … */
  passo: string;
  /** true = occupa tutta la riga (nota, immagine, capitolo). */
  pieno: boolean;
  /** true = il valore si ricalcola da solo dalla sua fonte. */
  collegato: boolean;
  /** Etichetta del tipo di blocco, come compare sulla pastiglia. */
  etichetta: string;
  /** Scheda o libreria di provenienza, se il blocco ne ha una. */
  provenienza: string;
  /** Nota che accompagna la fonte (la spiegazione della formula). */
  nota: string;
  /* ── solo per i blocchi di calcolo ── */
  nome: string;
  /** Formula come è scritta, con i nomi delle grandezze. */
  espressione: string;
  /** Numero da mostrare, letto nell'unità `um`. */
  valore: number;
  /** Valore in unità base: è quello che vedono i blocchi successivi. */
  valoreBase: number;
  um: string;
  /** Unità ricavata dall'operazione, quando non se n'è scelta una. */
  umAuto: string;
  /** Unità che porta la fonte (la formula preimpostata, la grandezza, l'import). */
  umFonte: string;
  /** Unità fra cui si può scegliere per leggere questo risultato. */
  umAmmesse: string[];
  /** true = il valore è un dato scritto, non il risultato di un'operazione:
   *  l'unità dà la scala al numero invece di convertirlo. */
  dato: boolean;
  dim: Dim | null;
  /** Testo al posto del numero (esiti delle verifiche). */
  testo: string;
  errore: string;
  /** Grandezze che servirebbero e non ci sono ancora. */
  mancanti: string[];
  /** true = il nome non entra fra le variabili (vuoto, già usato, non ammesso). */
  nomeIgnorato: boolean;
}

const VUOTO = {
  collegato: false,
  etichetta: '',
  provenienza: '',
  nota: '',
  nome: '',
  espressione: '',
  valore: NaN,
  valoreBase: NaN,
  um: '',
  umAuto: '',
  umFonte: '',
  umAmmesse: [] as string[],
  dato: false,
  dim: null,
  testo: '',
  errore: '',
  mancanti: [] as string[],
  nomeIgnorato: false,
};

/** I blocchi che occupano tutta la riga: hanno un contenuto, non un numero. */
const PIENI: TipoBlocco[] = ['nota', 'immagine', 'linea', 'capitolo'];

/**
 * Due numeri sono «lo stesso numero» se differiscono meno di un miliardesimo
 * relativo: lo stesso valore passato per una conversione di unità può tornare
 * indietro con l'ultimo bit diverso, e non è un valore diverso.
 */
function stessoNumero(a: number, b: number): boolean {
  if (a === b) return true;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= 1e-9 * Math.max(Math.abs(a), Math.abs(b), 1e-12);
}

/**
 * Ricalcola il quaderno intero, in ordine. Ogni blocco vede le grandezze del
 * pannello e i risultati dei blocchi che lo precedono; un blocco con un nome
 * nuovo e valido diventa a sua volta una grandezza richiamabile.
 */
export function ricalcolaQuaderno(
  blocchi: BloccoQuaderno[],
  { voci, preimpostate, importi, elenco = UNITA_DEFAULT }: Sorgenti,
): BloccoCalcolato[] {
  const vars = variabili(voci);
  const unita = unitaVariabili(voci);
  const umVar = umVariabili(voci);

  return blocchi.map((blocco, i) => {
    const passo = String(i + 1).padStart(2, '0');
    const pieno = PIENI.includes(blocco.tipo);
    const base: BloccoCalcolato = { blocco, passo, pieno, ...VUOTO };

    if (blocco.tipo === 'nota') return { ...base, etichetta: 'nota' };
    if (blocco.tipo === 'linea') return { ...base, etichetta: 'capitolo del foglio', testo: blocco.testo.trim() };
    if (blocco.tipo === 'immagine') return { ...base, etichetta: 'schema' };
    if (blocco.tipo === 'capitolo')
      return { ...base, collegato: true, etichetta: 'capitolo', provenienza: blocco.fonte };

    /* ── i blocchi di calcolo: nome, formula e da dove viene il numero ── */

    let nome = blocco.nome.trim();
    let espressione = '';
    let umFonte = '';
    let etichetta = 'operazione';
    let provenienza = '';
    let nota = '';
    /** Valore già pronto (grandezza, import) invece di una formula da valutare. */
    let pronto: { valoreBase: number; dim: Dim; testo?: string } | null = null;

    if (blocco.tipo === 'valore') {
      const v = voci.find((x) => x.id === blocco.fonte);
      if (!v) return { ...base, etichetta: 'grandezza', errore: 'grandezza non più in elenco' };
      nome = v.nome.trim();
      espressione = v.espressione.trim();
      umFonte = v.umEffettiva;
      etichetta = 'grandezza';
      nota = v.nota;
      const testa = { ...base, collegato: true, etichetta, nome, espressione, nota, um: umFonte, umFonte };
      if (v.errore) return { ...testa, errore: v.errore };
      // una grandezza ancora da compilare è un promemoria, non un errore
      if (!espressione) return { ...testa, mancanti: [nome || 'il valore'] };
      pronto = { valoreBase: v.valoreBase, dim: v.dim ?? {} };
    } else if (blocco.tipo === 'operazione') {
      const p = preimpostate.find((x) => x.id === blocco.fonte);
      if (!p) return { ...base, etichetta, errore: 'formula non più in elenco' };
      nome = p.nome.trim();
      espressione = p.espressione.trim();
      umFonte = p.um.trim();
      provenienza = 'formule';
      nota = p.nota;
    } else if (blocco.tipo === 'formula') {
      espressione = blocco.espressione.trim();
      etichetta = 'formula';
    } else {
      const im = importi.find((x) => x.id === blocco.fonte);
      if (!im) return { ...base, etichetta: 'import', errore: 'valore non più disponibile' };
      nome = im.nome.trim();
      // il nome basta: come si chiama nella scheda di provenienza lo dice la nota
      espressione = '';
      nota = im.etichetta;
      umFonte = im.um;
      etichetta = 'import';
      provenienza = im.scheda;
      pronto = { valoreBase: inBase(im.valore, im.um), dim: dimUnita(im.um), testo: im.testo };
    }

    const umScelta = blocco.um.trim() || umFonte;

    // il valore: o è già pronto (grandezza, import) o si valuta la formula
    let valore = NaN;
    let dim: Dim | null = null;
    let errore = '';
    let mancanti: string[] = [];
    let testo = pronto?.testo ?? '';
    /** true = numero puro che viene da grandezze con unità: un rapporto. */
    let rapporto = false;

    if (pronto) {
      valore = pronto.valoreBase;
      dim = pronto.dim;
    } else if (!espressione) {
      errore = blocco.tipo === 'formula' ? '' : 'formula vuota';
      mancanti = blocco.tipo === 'formula' ? ['una formula'] : [];
    } else {
      mancanti = nomiMancanti(espressione, vars);
      if (mancanti.length) {
        // niente errore: mancano dei dati, non è sbagliata
      } else {
        const esito = valutaConUnita(espressione, vars, unita, umVar, elenco);
        if (esito.ok) {
          valore = esito.valore;
          dim = esito.dim;
          rapporto = esito.rapporto;
        } else errore = esito.errore;
      }
    }

    const letto = Number.isFinite(valore) ? leggiRisultato(valore, dim, umScelta, elenco, rapporto) : null;
    const dimFinale = letto ? letto.dim : (dim ?? dimUnita(umScelta));
    const um = letto ? letto.um : umScelta;

    // il nome entra fra le variabili solo se è nuovo: due `M` diversi
    // renderebbero ambiguo il richiamo, e il primo ha la precedenza
    const registrabile =
      !!nome && nomeAmmesso(nome) && !(nome in vars) && !!letto && Number.isFinite(letto.valoreBase);
    if (registrabile && letto) {
      vars[nome] = letto.valoreBase;
      unita[nome] = letto.dim ?? {};
      umVar[nome] = letto.um;
    }

    /**
     * L'avviso «questo nome è già usato più su» ha senso solo quando dietro allo
     * stesso nome c'è un **numero diverso**: allora sì che il richiamo più in
     * basso è ambiguo. Una grandezza tirata dal pannello, invece, *è* la sua
     * variabile — b sul foglio e b nel pannello sono lo stesso b — e segnalarla
     * come doppione era un falso allarme su ogni blocco collegato.
     */
    const doppioneVero =
      !!nome && !registrabile && !!letto && !stessoNumero(vars[nome], letto.valoreBase);

    return {
      blocco,
      passo,
      pieno,
      collegato: blocco.tipo !== 'formula',
      etichetta,
      provenienza,
      nota,
      nome,
      espressione,
      valore: letto ? letto.valore : NaN,
      valoreBase: letto ? letto.valoreBase : NaN,
      um,
      umAuto: letto?.umAuto ?? '',
      umFonte,
      umAmmesse: dimFinale ? unitaCompatibili(dimFinale, elenco) : [],
      dato: letto ? letto.dato : true,
      dim: dimFinale,
      testo,
      errore,
      mancanti,
      nomeIgnorato: doppioneVero,
    };
  });
}

/**
 * Riga di testo di un blocco di calcolo, per il «Copia» e per l'HTML:
 * `nome = formula = risultato unità`. È la stessa riga che si vede sul foglio.
 */
export function testoBlocco(b: BloccoCalcolato): string {
  const testa = b.nome ? `${b.nome} = ` : '';
  if (b.errore) return `${testa}${b.espressione} — errore: ${b.errore}`;
  if (b.mancanti.length) return `${testa}${b.espressione || '—'} — manca ${b.mancanti.join(', ')}`;
  if (b.testo) return `${testa}${b.testo}`;
  const um = b.um ? ` ${b.um}` : '';
  const numero = formattaIn(b.valore, b.um);
  // il secondo uguale serve solo se c'è un conto da mostrare: `b = 0,30 m` è
  // una definizione, `A = b*h = 0,09 mq` è una formula
  const formula = haOperazioni(b.espressione) ? `${b.espressione} = ` : '';
  return `${testa}${formula}${numero}${um}`;
}

/**
 * Come sta un rapporto di verifica letto in percento: sotto l'80 % c'è
 * margine, fra l'80 e il 100 si è al limite, oltre il 100 non passa. È il
 * semaforo che si mette a matita a fianco del numero, e vale solo dove il
 * numero è davvero una percentuale — sugli altri risultati non c'è nulla da
 * dire.
 */
export type LivelloEsito = '' | 'ok' | 'limite' | 'fuori';

export function livelloEsito(b: BloccoCalcolato): LivelloEsito {
  if (b.errore || b.mancanti.length || b.testo) return '';
  if (b.um.trim() !== '%' || !Number.isFinite(b.valore)) return '';
  if (b.valore > 100) return 'fuori';
  if (b.valore >= 80) return 'limite';
  return 'ok';
}

/** Larghezza minima di uno schema: sotto non si legge più niente. */
export const LARGHEZZA_MIN = 20;

/** Larghezza di uno schema riportata dentro i limiti; 0 = tutta la riga. */
export function larghezzaValida(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(Math.min(100, Math.max(LARGHEZZA_MIN, n)));
}

/** Quanti posti liberi la vecchia griglia ammetteva prima di un blocco. */
export const SALTO_MAX = 2 * COLONNE_FOGLIO;

/** Posti liberi prima di un blocco, riportati fra 0 e `SALTO_MAX`. */
export function saltoValido(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(SALTO_MAX, Math.round(n));
}

/** Colonne di un blocco salvato riportate fra 1 e 3; 0 = non ne aveva scelte. */
export function colonneValide(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(COLONNE_FOGLIO, Math.max(1, Math.round(n)));
}

/** Rimette in ordine i blocchi che arrivano da un salvataggio. */
export function normalizzaBlocchi(raw: Partial<BloccoQuaderno>[]): BloccoQuaderno[] {
  const tipi: TipoBlocco[] = [
    'valore',
    'operazione',
    'formula',
    'import',
    'nota',
    'immagine',
    'linea',
    'capitolo',
  ];
  return (Array.isArray(raw) ? raw : []).flatMap((b, i) => {
    const tipo = tipi.find((t) => t === b?.tipo);
    if (!tipo) return [];
    return [
      {
        id: b?.id || `q-${i}`,
        tipo,
        fonte: b?.fonte ?? '',
        nome: b?.nome ?? '',
        espressione: b?.espressione ?? '',
        um: b?.um ?? '',
        testo: b?.testo ?? '',
        appunto: b?.appunto ?? '',
        colonne: colonneValide(b?.colonne),
        salto: saltoValido(b?.salto),
        acapo: b?.acapo === true,
        img: typeof b?.img === 'string' ? b.img : '',
        larghezza: larghezzaValida(b?.larghezza),
      },
    ];
  });
}

/* ──────────────── una formula preimpostata che si porta dietro i suoi dati ──────────────── */

/**
 * Una formula preimpostata non è mai sola: `M = q·l²/8` senza `q` e senza `l`
 * non è un calcolo, è un promemoria. Prima, tirarla nel quaderno lasciava una
 * riga rossa «manca q, l» e toccava andarsi a cercare le due grandezze nel
 * pannello, aggiungerle, controllare l'unità e tornare indietro.
 *
 * Da qui in poi la formula arriva con il suo corredo: le grandezze che le
 * servono e che il foglio non ha ancora entrano **prima** di lei, nell'ordine
 * in cui la formula le nomina, così la riga nasce già col numero (o con il
 * campo pronto da riempire, che è la stessa cosa un attimo prima).
 *
 * Dove si va a prendere una grandezza che manca, in ordine:
 *
 *  1. **è già fra le grandezze del pannello** — allora si mette sul foglio la
 *     riga che la richiama, e basta: unità e nota le ha già;
 *  2. **sta nel catalogo** delle grandezze proposte (`b`, `l`, `q`, `E`, `J`,
 *     i pesi di volume…) — si aggiunge al pannello **con la sua unità di
 *     misura**, che è il punto: una `q` senza unità, o in kN/mq invece che in
 *     kN/m, fa tornare un numero sbagliato senza dire niente;
 *  3. **non si sa cosa sia** — e allora non si inventa. Il nome finisce fra i
 *     `da inventare`, che la scheda mostra come suggerimento: sei tu a dire
 *     cos'è e in che unità si scrive.
 */
export interface Corredo {
  /** Grandezze nuove da aggiungere al pannello, già con nome, unità e nota. */
  grandezze: VoceCalcolo[];
  /** Righe da mettere sul foglio prima della formula, nell'ordine giusto. */
  blocchi: BloccoQuaderno[];
  /**
   * Nomi che la formula richiama e che non si sanno da nessuna parte: né sul
   * foglio, né nel pannello, né nel catalogo. Sono quelli da proporre a chi
   * calcola — con l'unità, che è l'unica cosa che il codice non può indovinare.
   */
  daInventare: string[];
  /**
   * Grandezze che ci sono ma **senza unità di misura**: la formula gira lo
   * stesso, il numero però esce senza scala e non si converte più. Vale la
   * pena dirlo prima, non dopo.
   */
  senzaUnita: string[];
}

/** Che cosa sa il quaderno nel momento in cui la formula ci entra. */
export interface Contesto {
  /**
   * Le grandezze richiamabili dal pannello, quelle scritte a mano e quelle che
   * nascono dalle scelte di libreria (`fck`, `Wx`, …): tutte hanno un id, e
   * quindi possono diventare una riga del foglio senza inventare niente.
   */
  voci: VoceCalcolo[];
  /** I blocchi già sul foglio: quello che portano si conta come disponibile. */
  blocchi: BloccoCalcolato[];
  /** Le grandezze proposte, da cui pescare quelle che mancano con la loro unità. */
  catalogo: Omit<VoceCalcolo, 'id'>[];
}

/** Un nome è già disponibile fra questi, γ scritta con la g compresa? */
function fraQuesti(nome: string, nomi: string[]): string | undefined {
  const insieme = Object.fromEntries(nomi.filter(Boolean).map((n) => [n, true]));
  return risolviNome(nome, insieme);
}

/**
 * Prepara l'ingresso di una formula preimpostata nel quaderno: dice quali
 * grandezze aggiungere al pannello, quali righe mettere sul foglio prima della
 * formula e quali nomi non si sanno da dove prendere.
 *
 * Non tocca niente: restituisce solo le mosse: chi chiama le applica in un
 * colpo solo, e i test possono guardarle senza montare mezza scheda.
 */
export function corredoFormula(espressione: string, { voci, blocchi, catalogo }: Contesto): Corredo {
  const sulFoglio = blocchi.filter((b) => !b.pieno).map((b) => b.nome.trim());
  const nelPannello = voci.map((v) => v.nome.trim());

  const grandezze: VoceCalcolo[] = [];
  const nuoviBlocchi: BloccoQuaderno[] = [];
  const daInventare: string[] = [];
  const senzaUnita: string[] = [];

  for (const nome of nomiRichiesti(espressione)) {
    // `ans` è l'ultimo risultato: c'è sempre, non è una grandezza da aggiungere
    if (nome === 'ans') continue;
    // già sul foglio: la formula lo vede, non c'è niente da aggiungere
    if (fraQuesti(nome, [...sulFoglio, ...grandezze.map((g) => g.nome)])) continue;

    const daPannello = fraQuesti(nome, nelPannello);
    const voce = daPannello ? voci.find((v) => v.nome.trim() === daPannello) : undefined;
    if (voce) {
      if (!voce.um.trim()) senzaUnita.push(voce.nome.trim());
      nuoviBlocchi.push(nuovoBlocco('valore', { fonte: voce.id }));
      continue;
    }

    const dal = catalogo.find((g) => !!fraQuesti(nome, [g.nome.trim()]));
    if (dal) {
      // l'unità viene dal catalogo, ed è tutto il punto della cosa
      const g: VoceCalcolo = { ...dal, id: `calc-${nome}-${grandezze.length}` };
      grandezze.push(g);
      nuoviBlocchi.push(nuovoBlocco('valore', { fonte: g.id }));
      continue;
    }

    if (!daInventare.includes(nome)) daInventare.push(nome);
  }

  return { grandezze, blocchi: nuoviBlocchi, daInventare, senzaUnita };
}

/**
 * La riga porta un numero **scritto da qualcuno**, non calcolato: è una cella
 * editabile, e sul foglio si vede — velatura ocra — perché a colpo d'occhio la
 * differenza fra «questo l'ho deciso io» e «questo l'ha calcolato il foglio»
 * è la prima cosa che si cerca rileggendo un calcolo.
 *
 * Sono di questa specie la grandezza tirata dal pannello (il valore si scrive
 * lì) e la formula scritta a mano che non contiene operazioni — `b = 0,30 m`,
 * cioè una definizione. Un risultato che viene da un'altra scheda no: quello
 * si cambia dove è nato, non qui.
 */
export function bloccoVariabile(b: BloccoCalcolato): boolean {
  if (b.pieno) return false;
  if (b.blocco.tipo === 'valore') return !haOperazioni(b.espressione);
  if (b.blocco.tipo === 'formula') return !!b.blocco.espressione.trim() && !haOperazioni(b.blocco.espressione);
  return false;
}
